import React from 'react';

const useArticleListFilters = ({
  filters,
  handleFilterChange,
  tagId,
  collectionId,
  setTimePeriod,
  searchQuery,
  setSearchQuery,
  searchDebounceRef,
  selectedSources,
  setSelectedSources,
  selectedSentiments,
  setSelectedSentiments,
  selectedExposures,
  setSelectedExposures,
  selectedTags,
  setSelectedTags,
  selectedCollections,
  setSelectedCollections,
  selectedMediaTypes,
  setSelectedMediaTypes,
  selectedContentTypes,
  setSelectedContentTypes,
  selectedTiers,
  setSelectedTiers,
  selectedStatuses,
  setSelectedStatuses,
  selectedEntityType,
  setSelectedEntityType,
  setShowFavoritesOnly,
  activeBrandData,
  dateOptions,
  tierOptions,
  statusOptions,
  exposureOptions
}) => {
  const handleSearch = React.useCallback((searchTerm) => {
    setSearchQuery(searchTerm);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      handleFilterChange({ ...filters, search: searchTerm });
    }, 400);
  }, [filters, handleFilterChange, searchDebounceRef, setSearchQuery]);

  const handleClearFilters = React.useCallback(() => {
    setSearchQuery('');
    setSelectedSources([]);
    setSelectedSentiments([]);
    setSelectedExposures([]);
    setSelectedTags([]);
    setSelectedCollections([]);
    setSelectedMediaTypes([]);
    setSelectedContentTypes([]);
    setSelectedTiers([]);
    setSelectedStatuses([]);
    setSelectedEntityType('');
    setShowFavoritesOnly(false);

    const defaultPeriod = (tagId || collectionId) ? 'all' : '24h';
    setTimePeriod(defaultPeriod);
    localStorage.setItem('article_timePeriod', defaultPeriod);

    handleFilterChange({
      search: '',
      date: '',
      siteName: '',
      sentiment: '',
      exposure: '',
      tags: '',
      collections: '',
      mediaType: '',
      contentType: '',
      tier: '',
      editorialOccupancyStatus: 'all',
      qualitativeAnalysisStatus: 'all',
      entityType: '',
      sort: 'date_desc',
      startDate: null,
      endDate: null,
      dateType: null,
    });
  }, [
    collectionId,
    handleFilterChange,
    setSearchQuery,
    setSelectedCollections,
    setSelectedContentTypes,
    setSelectedEntityType,
    setSelectedMediaTypes,
    setSelectedSentiments,
    setSelectedExposures,
    setSelectedSources,
    setSelectedStatuses,
    setSelectedTags,
    setSelectedTiers,
    setShowFavoritesOnly,
    setTimePeriod,
    tagId
  ]);

  const handleStatusesChange = React.useCallback((statuses) => {
    const aiStatusValues = ['kpia_applied', 'kpia_pending'];
    let nextStatuses = statuses;
    const selectedAiStatuses = statuses.filter(status => aiStatusValues.includes(status));

    if (selectedAiStatuses.length > 1) {
      const previousAiStatus = selectedStatuses.find(status => aiStatusValues.includes(status));
      const newestAiStatus = selectedAiStatuses.find(status => status !== previousAiStatus) || selectedAiStatuses[selectedAiStatuses.length - 1];
      nextStatuses = statuses.filter(status => !aiStatusValues.includes(status) || status === newestAiStatus);
    }

    setSelectedStatuses(nextStatuses);

    const newFilters = { ...filters };
    newFilters.isRead = 'all';
    newFilters.isFavorite = 'all';
    newFilters.hasTags = 'all';
    newFilters.editorialOccupancyStatus = 'all';
    newFilters.qualitativeAnalysisStatus = 'all';

    nextStatuses.forEach(status => {
      if (status === 'unread') newFilters.isRead = false;
      if (status === 'read') newFilters.isRead = true;
      if (status === 'favorite') newFilters.isFavorite = true;
      if (status === 'not_favorite') newFilters.isFavorite = false;
      if (status === 'no_tags') newFilters.hasTags = false;
      if (status === 'with_tags') newFilters.hasTags = true;
      if (status === 'editorial_pending') newFilters.editorialOccupancyStatus = 'pending';
      if (status === 'editorial_complete') newFilters.editorialOccupancyStatus = 'complete';
      if (status === 'kpia_applied') newFilters.qualitativeAnalysisStatus = 'applied';
      if (status === 'kpia_pending') newFilters.qualitativeAnalysisStatus = 'pending';
    });

    handleFilterChange(newFilters);
  }, [filters, handleFilterChange, selectedStatuses, setSelectedStatuses]);

  const handleToggleFavorites = React.useCallback(() => {
    setShowFavoritesOnly(prev => {
      const isFav = !prev;
      handleFilterChange({ ...filters, isFavorite: isFav ? true : 'all' });
      return isFav;
    });
  }, [filters, handleFilterChange, setShowFavoritesOnly]);

  const handleRemoveFilter = React.useCallback((filterKey) => {
    switch (filterKey) {
      case 'date':
        handleFilterChange({ ...filters, date: '', startDate: null, endDate: null, dateType: null });
        break;
      case 'siteName':
        setSelectedSources([]);
        handleFilterChange({ ...filters, siteName: '' });
        break;
      case 'sentiment':
        setSelectedSentiments([]);
        handleFilterChange({ ...filters, sentiment: '' });
        break;
      case 'exposure':
        setSelectedExposures([]);
        handleFilterChange({ ...filters, exposure: '' });
        break;
      case 'tags':
        setSelectedTags([]);
        handleFilterChange({ ...filters, tags: '' });
        break;
      case 'collections':
        setSelectedCollections([]);
        handleFilterChange({ ...filters, collections: '' });
        break;
      case 'mediaType':
        setSelectedMediaTypes([]);
        handleFilterChange({ ...filters, mediaType: '' });
        break;
      case 'contentType':
        setSelectedContentTypes([]);
        handleFilterChange({ ...filters, contentType: '' });
        break;
      case 'tier':
        setSelectedTiers([]);
        handleFilterChange({ ...filters, tier: '' });
        break;
      case 'entityType':
        setSelectedEntityType('');
        handleFilterChange({ ...filters, entityType: '' });
        break;
      case 'status':
        setSelectedStatuses([]);
        handleFilterChange({
          ...filters,
          isRead: 'all',
          isFavorite: 'all',
          hasTags: 'all',
          editorialOccupancyStatus: 'all',
          qualitativeAnalysisStatus: 'all'
        });
        break;
      case 'search':
        setSearchQuery('');
        handleFilterChange({ ...filters, search: '' });
        break;
      default:
        break;
    }
  }, [
    filters,
    handleFilterChange,
    setSearchQuery,
    setSelectedCollections,
    setSelectedContentTypes,
    setSelectedEntityType,
    setSelectedMediaTypes,
    setSelectedSentiments,
    setSelectedExposures,
    setSelectedSources,
    setSelectedStatuses,
    setSelectedTags,
    setSelectedTiers
  ]);

  const activeFilters = React.useMemo(() => {
    const nextActiveFilters = [];

    if (filters.date) {
      if (filters.date === 'custom') {
        let dateRangeText = 'Personalizado';
        if (filters.startDate || filters.endDate) {
          const formatDate = (date) => {
            if (!date) return '';
            return new Date(date).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          };

          const start = formatDate(filters.startDate);
          const end = formatDate(filters.endDate);

          if (start && end) {
            dateRangeText = `${start} - ${end}`;
          } else if (start) {
            dateRangeText = `De ${start}`;
          } else if (end) {
            dateRangeText = `Até ${end}`;
          }
        }

        nextActiveFilters.push({
          key: 'date',
          label: 'Data',
          value: dateRangeText
        });
      } else {
        const dateOption = dateOptions.find(opt => opt.value === filters.date);
        if (dateOption) {
          nextActiveFilters.push({
            key: 'date',
            label: 'Data',
            value: dateOption.label
          });
        }
      }
    }

    if (selectedSources.length > 0) {
      nextActiveFilters.push({ key: 'siteName', label: 'Fonte', value: selectedSources.join(', ') });
    }

    if (selectedSentiments.length > 0) {
      nextActiveFilters.push({ key: 'sentiment', label: 'Sentimento', value: selectedSentiments.join(', ') });
    }

    if (selectedExposures.length > 0) {
      const exposureLabels = selectedExposures.map(exposure => {
        const option = exposureOptions.find(item => item.value === exposure);
        return option ? option.label : exposure;
      });
      nextActiveFilters.push({ key: 'exposure', label: 'Exposição', value: exposureLabels.join(', ') });
    }

    if (selectedTags.length > 0) {
      nextActiveFilters.push({ key: 'tags', label: 'Tags', value: selectedTags.join(', ') });
    }

    if (selectedCollections.length > 0) {
      nextActiveFilters.push({ key: 'collections', label: 'Coleções', value: selectedCollections.join(', ') });
    }

    if (selectedMediaTypes.length > 0) {
      nextActiveFilters.push({ key: 'mediaType', label: 'Plataforma', value: selectedMediaTypes.join(', ') });
    }

    if (selectedContentTypes.length > 0) {
      nextActiveFilters.push({ key: 'contentType', label: 'Formato', value: selectedContentTypes.join(', ') });
    }

    if (selectedTiers.length > 0) {
      const tierLabels = selectedTiers.map(tier => {
        const tierOption = tierOptions.find(opt => opt.value === tier);
        return tierOption ? tierOption.label : tier;
      });
      nextActiveFilters.push({ key: 'tier', label: 'Tier', value: tierLabels.join(', ') });
    }

    if (selectedStatuses.length > 0) {
      const statusLabels = selectedStatuses.map(status => {
        const statusOption = statusOptions.find(opt => opt.value === status);
        return statusOption ? statusOption.label : status;
      });
      nextActiveFilters.push({ key: 'status', label: 'Status', value: statusLabels.join(', ') });
    }

    if (searchQuery) {
      nextActiveFilters.push({ key: 'search', label: 'Busca', value: searchQuery });
    }

    if (selectedEntityType) {
      const entityLabel = selectedEntityType === 'brand' ? (activeBrandData?.name || 'Minha Marca') : 'Concorrentes';
      nextActiveFilters.push({ key: 'entityType', label: 'Tipo de Marca', value: entityLabel });
    }

    return nextActiveFilters;
  }, [
    activeBrandData?.name,
    dateOptions,
    filters,
    searchQuery,
    selectedCollections,
    selectedContentTypes,
    selectedEntityType,
    selectedMediaTypes,
    selectedSentiments,
    selectedExposures,
    selectedSources,
    selectedStatuses,
    selectedTags,
    selectedTiers,
    statusOptions,
    tierOptions,
    exposureOptions
  ]);

  return {
    activeFilters,
    handleSearch,
    handleClearFilters,
    handleStatusesChange,
    handleToggleFavorites,
    handleRemoveFilter
  };
};

export default useArticleListFilters;
